import React, { useEffect, useState } from 'react';

import { usePdpContext } from '../../pdp-provider';

import { getOptionArraysFromProducts } from './util';
import OtherOptions from './other-options';
import OptionPickerByFormCode from '../product-options-fields/option-picker-by-form-code';

/*
 * As we loop through the product option category children,
 * if a product child is in the default or modified recipe,
 * we lift it out and display it as an active editable widget.
 *
 * To keep the interface simpler, for category child products that are
 * not in the recipe, we'll hide those behind a dropdown
 * like "Add <subcategory name>" (e.g. "Add toppings")
 * for the user to be able to expand and see those child options separately.
 * This helps keep the list of available options less overwhelming looking.
 */

const CustomizationCategory = ({ category, topLevelCategory }) => {
  // Most categories have subcategories, which are represented as 'children'.
  // Recurse through the hierarchy until we are working with products, not children.
  if (category.children?.length > 0) {
    return category.children.map((child) => {
      return (
        <CustomizationCategory
          category={child}
          key={child.name}
          topLevelCategory={category}
        />
      );
    });
  }
  const [state, setState] = useState({
    singlePickerOptions: [],
    otherOptions: [],
  });

  // if we recursed into childCategories, we held onto the topLevelCategory
  // so we can use its name in the UI
  const topLevelCategoryName = topLevelCategory
    ? topLevelCategory.name
    : category.name;
  const topLevelCategoryNumber = topLevelCategory
    ? topLevelCategory.categoryNumber
    : category.categoryNumber;
  const categoryKeyPath = topLevelCategory
    ? [topLevelCategory.categoryNumber, category.categoryNumber]
    : [category.categoryNumber];
  const { customerRecipe } = usePdpContext();

  useEffect(() => {
    // break the category into options which need to be shown in a picker already,
    // vs those that get buried under a Select like 'Add toppings'
    const { singlePickerOptions, otherOptions } = getOptionArraysFromProducts(
      category,
      customerRecipe
    );
    setState({
      singlePickerOptions,
      otherOptions,
    });
  }, [customerRecipe]);

  return (
    <React.Fragment>
      {state.singlePickerOptions.map(({ option, isInRecipe }) => {
        // because the recipe options gets a more complete data shape from the
        // customerRecipe object, the keys are a bit different.
        const formCode = isInRecipe ? option?.formCode : option?.form?.formCode;
        const productOption = isInRecipe ? option?.productOption : option;
        return (
          <OptionPickerByFormCode
            category={category}
            categoryKeyPath={categoryKeyPath}
            formCode={formCode}
            key={`${productOption?.productNumber}-${formCode}`}
            productOption={productOption}
            topLevelCategoryName={topLevelCategoryName}
            topLevelCategoryNumber={topLevelCategoryNumber}
            // "gray out" the picker if not in the active recipe
            useInactiveStyle={(isInRecipe && option.deselected) || !isInRecipe}
          />
        );
      })}
      {/*
        For categories that have more than one child/option, they'll get hidden
        behind a dropdown and turned into pickers on selecting the child.
      */}
      {state.otherOptions.length > 1 ? (
        <OtherOptions
          category={category}
          categoryKeyPath={categoryKeyPath}
          productOptions={state.otherOptions}
          topLevelCategoryName={topLevelCategoryName}
          topLevelCategoryNumber={topLevelCategoryNumber}
        />
      ) : null}
    </React.Fragment>
  );
};

export default CustomizationCategory;
